import clsx from 'clsx';
import React, { useEffect, useState, useCallback } from 'react';
import ReactGA from 'react-ga4';
import { Box, Grid, Link, Typography, Button, CircularProgress } from '@mui/material';
import { PlanEditableCreationsEnum, ShapesEnum, UserTypeEnum } from 'enums';
import { useCreations } from 'contexts/useCreations';
import useCreationCreate, { makeRequestPayloadFromShape } from 'hooks/useCreationCreate';
import CustomSizeForm from '../CustomSizeForm';
import { useStyles } from './styles';
import {
  blackNovemberPromotionData,
  flowTrialActive,
  getEnv,
  isAnniversaryActive,
  userIsAvailableForNewTrialFlow,
} from 'utils';
import BlackNovemberLimitModal from '../BlackNovemberLimitModal';
import AnniversaryMindTheGraphModal from '../AnniversaryMindTheGraphModal';
import LimitationModal from '../LimitationModal';
import ProfileService from 'services/ProfileService';
import { checkUserCountry } from 'utils/locale';
import FlowTrialModal from '../FlowTrialModal';
import { useRouter } from 'next/router';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';
import BaseModal from 'components/BaseModal';
import { templateSizes } from 'utils/workspace/templateSizes';
import { COLOR_PINK } from 'styles/variables/colors';

export default function ShapesBox({ isOpen, onClose }) {
  const classes = useStyles();
  const { user, userJournalSettings } = useAuthUser();
  const [allCreations] = useCreations();
  const { createCreationFromShape, createCreationFromShapeWithContinueUrl } = useCreationCreate();
  const [activeShape, setActiveShape] = useState();
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const router = useRouter();
  const promotionBlacknovember = blackNovemberPromotionData();
  const [userIsChina, setUserIsChina] = useState(false);
  const [userIsJapan, setUserIsJapan] = useState(false);
  const [lastInvoice, setLastInvoice] = useState();
  const [modalTrial, setModalTrial] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const clevertap = getCleverTap();
  const userAvailableForTrialFlow =
    flowTrialActive &&
    !lastInvoice &&
    userIsAvailableForNewTrialFlow(user) &&
    !(userIsChina || userIsJapan);
  const disableNewCreation =
    user.tp_user === UserTypeEnum.JOURNAL &&
    allCreations.length >= userJournalSettings.creationsAvailable;

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  async function getLastInvoice() {
    const profileService = new ProfileService();
    const { data: lastInvoice } = await profileService.getLastInvoice();

    setLastInvoice(lastInvoice);
  }

  async function getCountryFromUser() {
    const isChina = await checkUserCountry.isChina(user);
    const isJapan = await checkUserCountry.isJapan(user);

    setUserIsChina(isChina);
    setUserIsJapan(isJapan);
  }

  function handleRedirectToOldWorkspace(shape, width, height) {
    const shapeData = makeRequestPayloadFromShape(shape, width, height);
    let url = `${getEnv('MTG_URL_LEGACY')}/workspace/create_base_slide/${shapeData.themeSlug}`;

    if (shapeData.width && shapeData.height) {
      url = `${getEnv('MTG_URL_LEGACY')}/workspace/create_base_slide/${shapeData.themeSlug}/${
        shapeData.width
      }/${shapeData.height}`;
    }

    window.location.href = url;
  }

  function sendTrackingEvents() {
    ReactGA.event({
      category: 'workspace',
      action: 'new_creation_my_creations',
      label: 'Click on any format option or "Create" button in My Creations',
      value: user.id,
    });
  }

  async function handleShapeClick(shape, width = null, height = null, redirectionPath = null) {
    if (
      userAvailableForTrialFlow &&
      allCreations.length >= PlanEditableCreationsEnum[user.tp_user]
    ) {
      setModalTrial(true);
      return;
    }

    if (allCreations.length >= PlanEditableCreationsEnum[user.tp_user]) {
      ReactGA.event({
        category: 'workspace',
        action: 'new_creation_upgrade_banner',
        label: 'When Banner Presented to free users',
        value: user.id,
      });

      clevertap.event.push('Banner Click', {
        Product: 'MTG',
        Email: user.email,
        URL: router.asPath,
        Description: 'New Creation Upgrade Banner',
        Locale: 'my-creations',
      });

      setLimitModalOpen(true);

      return;
    }

    if (!user.useNewWorkspace) {
      handleRedirectToOldWorkspace(shape, width, height);

      return;
    }

    try {
      setActiveShape(shape);

      clevertap.event.push('New Creation', {
        Product: 'MTG',
        Email: user.email,
        URL: router.asPath,
        Description: 'New Creation Button',
        Locale: 'my-creations',
        Type: shape,
      });

      if (redirectionPath) {
        await createCreationFromShapeWithContinueUrl(shape, width, height, redirectionPath);
      } else {
        await createCreationFromShape(shape, width, height);
      }
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setActiveShape();
    }
  }

  const handleLimitModalClose = useCallback(() => {
    setLimitModalOpen(false);
  }, []);

  const modalToRender = React.useMemo(() => {
    if (!limitModalOpen) return null;

    if (promotionBlacknovember.isActive) {
      return <BlackNovemberLimitModal open={limitModalOpen} onClose={handleLimitModalClose} />;
    }

    if (isAnniversaryActive()) {
      return <AnniversaryMindTheGraphModal open={limitModalOpen} onClose={handleLimitModalClose} />;
    }

    if (!promotionBlacknovember.isActive && !isAnniversaryActive()) {
      return <LimitationModal open={limitModalOpen} onClose={handleLimitModalClose} />;
    }
  }, [limitModalOpen, promotionBlacknovember.isActive, handleLimitModalClose]);

  useEffect(() => {
    getLastInvoice();
    getCountryFromUser();
  }, [user]);

  return (
    <>
      {modalTrial && <FlowTrialModal open={modalTrial} onClose={() => setModalTrial(false)} />}

      {modalToRender}

      <BaseModal
        size="md"
        open={isOpen}
        header={
          <div className={classes.header}>
            <span>What would you like to create today?</span>
            <Link
              sx={{ textDecoration: 'none', opacity: templateLoading ? 0.5 : 1 }}
              onClick={async () => {
                setTemplateLoading(true);
                await handleShapeClick(
                  templateSizes[0].shape,
                  templateSizes[0].width,
                  templateSizes[0].height,
                  '?template=true',
                );
                setTemplateLoading(false);
              }}
              disabled={disableNewCreation || templateLoading}
            >
              {templateLoading && <CircularProgress size={20} sx={{ mr: 1, color: COLOR_PINK }} />}
              <span className="headerLink">Start with a template</span>
            </Link>
          </div>
        }
        classes={{
          actions: classes.actionsOverride,
        }}
        fill={true}
        content={
          <Box>
            <Box spacing={2}>
              <Grid className={classes.borderRightMd} item={true} xs={12} md={9}>
                <Grid container={true} className={classes.sizeWrapper}>
                  {templateSizes.map((size) => (
                    <Grid key={size.id} item={true} xs={12} sm={3}>
                      <div
                        className={classes.shapesBoxItem}
                        onClick={() => {
                          if (!!!activeShape && !disableNewCreation) {
                            sendTrackingEvents();
                            handleShapeClick(size.shape);
                          }
                        }}
                      >
                        <div className={classes.shapeButtonContainer}>
                          <Button
                            classes={{
                              root: clsx(classes.shapeButton, classes[size.shape.toLowerCase()]),
                              disabled: classes.shapeButtonDisabled,
                            }}
                            variant="outlined"
                          >
                            {activeShape === size.shape && !!templateLoading === false ? (
                              <CircularProgress className={classes.shapeButtonProgress} size={25} />
                            ) : (
                              <img alt="Plus icon" className={classes.shapeButtonImage} />
                            )}
                          </Button>
                        </div>

                        <Typography className={classes.shapeTitle}>
                          <b>{size.label}</b>
                        </Typography>
                        <Typography>
                          {size.width} x {size.height} px
                        </Typography>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Box className={classes.customHeader}>
                <Typography className={classes.customTitle}>
                  Looking for a specific creation size?
                </Typography>
                <div
                  onClick={toggleVisibility}
                  className={`${classes.arrow} ${isVisible ? classes.arrowUp : ''}`}
                ></div>
              </Box>

              {isVisible && (
                <Grid item={true} xs={12} md={2}>
                  <Box textAlign="center" px={1}>
                    <CustomSizeForm
                      variant="outlined"
                      color="primary"
                      busy={activeShape === ShapesEnum.CUSTOM}
                      disabled={!!activeShape || disableNewCreation}
                      onSubmit={(width, height) =>
                        handleShapeClick(ShapesEnum.CUSTOM, width, height)
                      }
                    />
                  </Box>
                </Grid>
              )}
            </Box>
          </Box>
        }
        cancelButton={false}
        actionButton={false}
        onClose={onClose}
      />
    </>
  );
}
